import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";

import { colors } from "../../colors";
import type { SortableStyleProps } from "./types";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const SortableContainer = styled.div<SortableStyleProps>`
  display: flex;
  flex-direction: ${({ direction = "vertical" }) =>
    direction === "horizontal" ? "row" : "column"};
  gap: 10px;

  ${({ direction = "vertical" }) =>
    direction === "horizontal" &&
    css`
      flex-wrap: wrap;
      align-items: flex-start;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `}
  
  animation: ${fadeIn} ${({ animationDuration = 200 }) =>
    animationDuration}ms ease-in-out;
`;

export interface SortableItemWrapperProps {
  $isDragging?: boolean;
  $isOver?: boolean;
  $transform?: string;
  $transition?: string;
  disabled?: boolean;
}

export const SortableItemWrapper = styled.div<SortableItemWrapperProps>`
  position: relative;
  transform: ${({ $transform }) => $transform || "none"};
  transition: ${({ $transition }) => $transition || "none"};
  cursor: ${({ disabled }) => (disabled ? "default" : "grab")};

  ${({ $isDragging }) =>
    $isDragging &&
    css`
      opacity: 0.5;
      cursor: grabbing;
      z-index: 1000;
    `}

  &:active {
    cursor: ${({ disabled }) => (disabled ? "default" : "grabbing")};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
    `}
`;

export const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: grab;
  color: ${colors.gray[500]};
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.cyan[600]};
  }

  &:active {
    cursor: grabbing;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SortableContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SortableItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid ${colors.gray[200]};
  background: ${colors.white};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.primary[300]};
  }

  &:focus-within {
    outline: 2px solid ${colors.cyan[600]};
    outline-offset: 2px;
  }
`;
